import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  Loader2,
  QrCode,
  RefreshCw,
  Send,
  XCircle,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useReceptionCollection } from "@/hooks/useReceptionCollection";
import { getApiErrorMessage } from "@/lib/api";
import type { ReceptionNotificationChannel } from "@/types/appointments";

interface ReceptionPaymentLinkPanelProps {
  appointmentId: string;
  amountPaise?: number;
  currency?: string;
  onFinished: () => void;
  onRestart: () => void;
  onContinueLater?: () => void;
}

const channels: ReceptionNotificationChannel[] = ["sms", "whatsapp", "email"];
const terminalStates = new Set(["paid", "cancelled", "expired", "failed"]);

const labels: Record<string, string> = {
  creating: "Preparing payment link",
  awaiting_payment: "Awaiting payment",
  resolving_payment: "Payment window ended — resolving",
  payment_in_progress: "Payment is in progress",
  cancellation_pending: "Cancellation is being verified",
  resolution_pending: "Provider confirmation pending",
  paid: "Paid and confirmed",
  cancelled: "Cancelled — slot released",
  expired: "Expired, not paid — slot released",
  failed: "Payment link setup failed",
};

const reasonText: Record<string, string> = {
  provider_create_unknown:
    "Razorpay may have created the link, but Columbus did not receive a conclusive response. The slot remains protected while the same reference is reconciled.",
  provider_create_window_elapsed:
    "The original link deadline no longer has Razorpay's minimum lifetime. No replacement link was created; the slot remains protected.",
  provider_unreachable:
    "Razorpay is temporarily unreachable. The slot remains protected and reconciliation will retry.",
  provider_state_unknown:
    "Razorpay did not return conclusive payment evidence. The slot has not been released.",
  payment_in_progress:
    "A payment may be authorised or in progress. Capture always wins, so the slot remains protected.",
  captured_payment_missing:
    "Razorpay reports the link paid, but captured-payment evidence is still being resolved.",
  cancellation_pending:
    "Cancellation was requested. The slot releases only after Razorpay confirms the link closed and unpaid.",
};

const formatRemaining = (deadline: string): string => {
  const seconds = Math.max(
    0,
    Math.ceil((new Date(deadline).getTime() - Date.now()) / 1000)
  );
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

const formatTimestamp = (value: string | null): string =>
  value ? new Date(value).toLocaleString() : "Not checked yet";

export const ReceptionPaymentLinkPanel = ({
  appointmentId,
  amountPaise,
  currency = "INR",
  onFinished,
  onRestart,
  onContinueLater,
}: ReceptionPaymentLinkPanelProps) => {
  const { toast } = useToast();
  const collection = useReceptionCollection(appointmentId);
  const [channel, setChannel] = useState<ReceptionNotificationChannel>("sms");
  const [email, setEmail] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [, setTick] = useState(0);
  const snapshot = collection.snapshot.data;
  const attempt = snapshot?.attempt;
  const state = snapshot?.state ?? "creating";
  const amount = snapshot?.order?.amount_paise ?? amountPaise ?? 0;
  const checkoutPricing = snapshot?.pricing ?? {
    currency: snapshot?.order?.currency ?? currency,
    subtotal_paise: amount,
    discount_paise: 0,
    payable_paise: amount,
    promo_code: null,
  };
  const safeReason = snapshot?.session.safe_reason ?? attempt?.safe_error_code;
  const can = (action: string) =>
    snapshot?.allowed_actions.includes(action) ?? false;
  const notification = useMemo(
    () => snapshot?.notifications[channel],
    [channel, snapshot?.notifications]
  );

  useEffect(() => {
    if (terminalStates.has(state)) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [state]);

  const run = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      toast({ title: "Updated", description: message });
      return true;
    } catch (error) {
      toast({
        title: "Action unavailable",
        description: getApiErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
      return false;
    }
  };

  if (collection.snapshot.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading payment status…
      </div>
    );
  }

  if (collection.snapshot.error || !snapshot) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to load payment status</AlertTitle>
        <AlertDescription className="mt-3 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void collection.snapshot.refetch()}
          >
            Try again
          </Button>
          <Button variant="outline" size="sm" onClick={onRestart}>
            Back to appointments
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (state === "paid" && snapshot.confirmation) {
    const confirmation = snapshot.confirmation;
    const pricing = confirmation.pricing ?? checkoutPricing;
    return (
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="space-y-3">
          <div className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 p-4">
            <CheckCircle2 className="mt-0.5 h-6 w-6 text-success" />
            <div>
              <CardTitle className="z-h2 text-foreground">
                Payment received — appointment confirmed
              </CardTitle>
              <p className="z-secondary mt-1 text-muted-foreground">
                The payment was captured and the appointment is confirmed.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 rounded-2xl border border-border/60 bg-muted/15 p-4 sm:grid-cols-2">
            <div>
              <p className="z-caption text-muted-foreground">Patient</p>
              <p className="font-medium text-foreground">
                {confirmation.patient_name ?? "—"}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">Appointment</p>
              <p className="font-medium text-foreground">
                {new Date(confirmation.appointment_start_time).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">Service</p>
              <p className="font-medium text-foreground">
                {confirmation.service_name ?? "—"}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">Doctor</p>
              <p className="font-medium text-foreground">
                {confirmation.doctor_name ?? "—"}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">Location</p>
              <p className="font-medium text-foreground">
                {confirmation.clinic_name ??
                  (confirmation.appointment_type === "teleconsultation"
                    ? "Online consultation"
                    : "—")}
              </p>
            </div>
          </div>

          <div className="space-y-2 rounded-2xl border border-border/60 p-4 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Consultation fee</span>
              <span>₹{(pricing.subtotal_paise / 100).toFixed(2)}</span>
            </div>
            {pricing.discount_paise > 0 && (
              <div className="flex justify-between text-success">
                <span>Promo {pricing.promo_code}</span>
                <span>-₹{(pricing.discount_paise / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border/60 pt-2 font-semibold text-foreground">
              <span>Amount paid</span>
              <span>₹{(pricing.payable_paise / 100).toFixed(2)}</span>
            </div>
          </div>

          <div className="grid gap-3 rounded-2xl border border-border/60 p-4 text-sm sm:grid-cols-2">
            <div>
              <p className="text-muted-foreground">Appointment ID</p>
              <p className="break-all font-mono text-xs text-foreground">
                {confirmation.zarity_appointment_id ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment ID</p>
              <p className="break-all font-mono text-xs text-foreground">
                {confirmation.provider_payment_id ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Paid at</p>
              <p className="text-foreground">
                {formatTimestamp(confirmation.confirmed_at)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment method</p>
              <p className="text-foreground">
                {confirmation.payment_method ?? "—"}
              </p>
            </div>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <Button onClick={onFinished}>Done</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
      <CardHeader className="space-y-3">
        <CardTitle className="z-h2 flex flex-wrap items-center justify-between gap-2 text-foreground">
          <span>Collect appointment payment</span>
          <Badge variant="outline">
            {currency} {(amount / 100).toFixed(2)}
          </Badge>
        </CardTitle>
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
          {state === "paid" ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
          ) : terminalStates.has(state) ? (
            <XCircle className="mt-0.5 h-5 w-5 text-destructive" />
          ) : (
            <Clock3 className="mt-0.5 h-5 w-5 text-primary" />
          )}
          <div>
            <p className="z-body-md text-foreground">
              {labels[state] ?? state.replaceAll("_", " ")}
            </p>
            <p className="z-secondary mt-1 text-muted-foreground">
              {safeReason && reasonText[safeReason]
                ? reasonText[safeReason]
                : state === "awaiting_payment"
                  ? "This page updates automatically. Payment confirmation will confirm the appointment."
                  : "The slot remains protected until the backend reaches a safe terminal result."}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!terminalStates.has(state) && (
          <div className="grid gap-3 rounded-2xl border border-border/60 bg-muted/15 p-4 sm:grid-cols-3">
            <div>
              <p className="z-caption text-muted-foreground">Link expires in</p>
              <p className="z-numeric mt-1 font-semibold text-foreground">
                {formatRemaining(snapshot.link_expires_at)}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">
                Slot held / final check in
              </p>
              <p className="z-numeric mt-1 font-semibold text-foreground">
                {formatRemaining(snapshot.slot_release_at)}
              </p>
            </div>
            <div>
              <p className="z-caption text-muted-foreground">
                Last provider check
              </p>
              <p className="z-numeric mt-1 font-semibold text-foreground">
                {formatTimestamp(snapshot.last_checked_at)}
              </p>
            </div>
          </div>
        )}

        {!terminalStates.has(state) && (
          <div className="space-y-2 rounded-2xl border border-border/60 p-4 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Consultation fee</span>
              <span>₹{(checkoutPricing.subtotal_paise / 100).toFixed(2)}</span>
            </div>
            {checkoutPricing.discount_paise > 0 && (
              <div className="flex justify-between text-success">
                <span>Promo {checkoutPricing.promo_code}</span>
                <span>
                  -₹{(checkoutPricing.discount_paise / 100).toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-border/60 pt-2 font-semibold text-foreground">
              <span>Amount to collect</span>
              <span>₹{(checkoutPricing.payable_paise / 100).toFixed(2)}</span>
            </div>
          </div>
        )}

        {attempt?.payment_page_url && !terminalStates.has(state) && (
          <div className="space-y-3 border-t border-border pt-4">
            <div className="break-all rounded-lg border border-border bg-muted/30 p-3 text-sm">
              {attempt.payment_page_url}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!can("open_payment_page")}
                onClick={() =>
                  window.open(
                    attempt.payment_page_url ?? "",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <ExternalLink className="mr-2 h-4 w-4" /> Open payment page
              </Button>
              <Button
                variant="outline"
                disabled={!can("show_qr")}
                onClick={() => setShowQr((value) => !value)}
              >
                <QrCode className="mr-2 h-4 w-4" />{" "}
                {showQr ? "Hide QR" : "Show QR"}
              </Button>
              <Button
                variant="outline"
                disabled={!can("copy_link")}
                onClick={() =>
                  void run(
                    () =>
                      navigator.clipboard.writeText(
                        attempt.payment_page_url ?? ""
                      ),
                    "Payment link copied."
                  )
                }
              >
                <Copy className="mr-2 h-4 w-4" /> Copy link
              </Button>
            </div>
            {state === "awaiting_payment" &&
              !channels.some((item) => can(`send_${item}`)) && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Payment messages are not configured</AlertTitle>
                  <AlertDescription>
                    Configure the dedicated Reception SMS/email template or
                    WhatsApp campaign on Columbus. Existing OTP configuration is
                    intentionally not reused.
                  </AlertDescription>
                </Alert>
              )}
            {showQr && can("show_qr") && (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-5 text-foreground">
                <QRCodeSVG
                  value={attempt.payment_page_url}
                  size={220}
                  level="M"
                  includeMargin
                />
                <p className="text-center text-xs text-muted-foreground">
                  This QR contains the same Payment Link shown above. No second
                  Razorpay object is created.
                </p>
              </div>
            )}
          </div>
        )}

        {attempt?.payment_page_url && !terminalStates.has(state) && (
          <div className="space-y-3 border-t border-border pt-4">
            <div>
              <p className="font-medium">Send or resend the same link</p>
              <p className="text-xs text-muted-foreground">
                “Sent” means Zarity's provider accepted the request; it is not
                an OTP or delivery receipt.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {channels.map((item) => (
                <Button
                  key={item}
                  size="sm"
                  variant={channel === item ? "default" : "outline"}
                  disabled={!can(`send_${item}`)}
                  onClick={() => setChannel(item)}
                >
                  {item}
                </Button>
              ))}
            </div>
            {channel === "email" && can("send_email") && (
              <Input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Patient email"
                type="email"
              />
            )}
            <p className="text-sm text-muted-foreground">
              {channel.toUpperCase()}: {notification?.state ?? "never_sent"} ·{" "}
              {notification?.attempt_count ?? 0} attempt(s)
              {notification?.safe_error_code
                ? ` · ${notification.safe_error_code}`
                : ""}
            </p>
            <Button
              size="sm"
              disabled={
                !can(`send_${channel}`) ||
                collection.notify.isPending ||
                (channel === "email" && !email)
              }
              onClick={() =>
                void run(
                  () =>
                    collection.notify.mutateAsync({
                      channel,
                      email: channel === "email" ? email : undefined,
                    }),
                  "Send request recorded. Status will update automatically."
                )
              }
            >
              {collection.notify.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {notification?.attempt_count ? "Resend link" : "Send link"}
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          {onContinueLater && !terminalStates.has(state) && (
            <div className="mr-auto flex min-w-full flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/30 px-3 py-2 sm:min-w-0">
              <p className="text-xs text-muted-foreground">
                The booking and payment link remain active.
              </p>
              <Button variant="secondary" size="sm" onClick={onContinueLater}>
                <Clock3 className="mr-2 h-4 w-4" /> Continue later
              </Button>
            </div>
          )}
          {!terminalStates.has(state) && (
            <Button
              variant="outline"
              size="sm"
              disabled={collection.refresh.isPending}
              onClick={() =>
                void run(
                  () => collection.refresh.mutateAsync(),
                  "Razorpay status checked."
                )
              }
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${collection.refresh.isPending ? "animate-spin" : ""}`}
              />
              Check Razorpay now
            </Button>
          )}
          {can("cancel_checkout") && (
            <Button
              variant="destructive"
              size="sm"
              disabled={collection.cancel.isPending}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel unpaid booking
            </Button>
          )}
          {state === "paid" && <Button onClick={onFinished}>Done</Button>}
          {can("rebook") && (
            <Button onClick={onRestart}>Rebook this slot</Button>
          )}
        </div>
      </CardContent>
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel unpaid appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              The slot will be released only after Razorpay confirms that the
              payment link is closed and unpaid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={collection.cancel.isPending}>
              Keep appointment
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={collection.cancel.isPending}
              onClick={(event) => {
                event.preventDefault();
                void run(
                  () => collection.cancel.mutateAsync(),
                  "Cancellation status updated."
                ).then((succeeded) => {
                  if (succeeded) setCancelDialogOpen(false);
                });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {collection.cancel.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Cancel appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
